import React, { useRef, Fragment } from 'react';
import { motion, useInView } from 'framer-motion';
const METRO_LOGO_URL = 'https://cdn.magicpatterns.com/uploads/83gAX3TsGvnbvH5ew774Zt/Metro_Logo_HQ26.png';
/* ------------------------------------------------------------------ */
/*  PRINT CANVAS — flat artwork frame (no garment silhouette)          */
/* ------------------------------------------------------------------ */
function PrintCanvas({
  bg,
  children,
  aspect = 'aspect-[4/5]',
  pad = 'p-8',
  textColor = '#0E0E12'
}: {
  bg: string;
  children: React.ReactNode;
  aspect?: string;
  pad?: string;
  textColor?: string;
}) {
  return <div className={`relative ${aspect} ${pad} flex items-center justify-center overflow-hidden`} style={{
    background: bg,
    color: textColor
  }} data-id="element-836">
      {/* Corner registration marks */}
      {['top-2 left-2', 'top-2 right-2', 'bottom-2 left-2', 'bottom-2 right-2'].map((p, i) => <div key={i} className={`absolute ${p} w-3 h-3 opacity-30`} style={{
      color: textColor
    }} data-id="element-837">
          <svg viewBox="0 0 12 12" fill="none" data-id="element-838">
            <line x1="6" y1="0" x2="6" y2="12" stroke="currentColor" strokeWidth="0.6" data-id="element-839" />
            <line x1="0" y1="6" x2="12" y2="6" stroke="currentColor" strokeWidth="0.6" data-id="element-840" />
            <circle cx="6" cy="6" r="2.5" stroke="currentColor" strokeWidth="0.6" data-id="element-841" />
          </svg>
        </div>)}
      <div className="w-full h-full flex items-center justify-center" data-id="element-842">
        {children}
      </div>
    </div>;
}
/* ------------------------------------------------------------------ */
/*  ITEM CARD                                                          */
/* ------------------------------------------------------------------ */
function ItemCard({
  number,
  title,
  meta,
  caption,
  inks,
  children
}: {
  number: string;
  title: string;
  meta: string;
  caption: string;
  inks: string[];
  children: React.ReactNode;
}) {
  return <div className="border border-[var(--divider)] rounded-sm overflow-hidden bg-white flex flex-col" data-id="element-843">
      <div className="relative" data-id="element-844">
        {children}
        <div className="absolute top-3 left-3 font-mono text-[9px] tracking-widest text-[var(--text-ink)] bg-white/85 backdrop-blur px-1.5 py-0.5 rounded-sm" data-id="element-845">
          {number}
        </div>
      </div>
      <div className="p-3 border-t border-[var(--divider)] flex flex-col gap-1.5" data-id="element-846">
        <div className="flex items-start justify-between gap-2" data-id="element-847">
          <div className="font-space font-bold text-sm tracking-tight leading-tight" data-id="element-848">
            {title}
          </div>
          <div className="flex items-center gap-1 mt-0.5" data-id="element-849">
            {inks.map(c => <span key={c} className="w-2.5 h-2.5 rounded-full border border-black/15" style={{
            background: c
          }} data-id="element-850" />)}
          </div>
        </div>
        <div className="font-mono text-[9px] text-[var(--text-muted)] tracking-widest" data-id="element-851">
          {meta}
        </div>
        <div className="text-[10px] text-[var(--text-muted)] leading-snug" data-id="element-852">
          {caption}
        </div>
      </div>
    </div>;
}
/* ------------------------------------------------------------------ */
/*  SHARED ART PRIMITIVES                                              */
/* ------------------------------------------------------------------ */
function LineBullet({
  label,
  bg,
  fg,
  size = 36
}: {
  label: string;
  bg: string;
  fg: string;
  size?: number;
}) {
  return <div className="flex items-center justify-center font-space font-bold rounded-full shrink-0" style={{
    background: bg,
    color: fg,
    width: size,
    height: size,
    fontSize: size * 0.42,
    letterSpacing: '-0.02em'
  }} data-id="element-853">
      {label}
    </div>;
}
function MetroLogoMark({
  height = 60,
  invert = false
}: {
  height?: number;
  invert?: boolean;
}) {
  return <img src={METRO_LOGO_URL} alt="Metro" style={{
    height,
    width: 'auto',
    filter: invert ? 'brightness(0) invert(1)' : undefined
  }} data-id="element-854" />;
}
function RouteMapHorizontal({
  color = '#1A2BFF',
  stops = ['BRAND', 'WEB', 'CONTENT', 'MARKETING', 'AI'],
  textColor = '#0E0E12'
}: {
  color?: string;
  stops?: string[];
  textColor?: string;
}) {
  return <svg viewBox="0 0 320 60" className="w-full" data-id="element-855">
      <line x1="20" y1="22" x2="300" y2="22" stroke={color} strokeWidth="4" strokeLinecap="round" data-id="element-856" />
      {stops.map((s, i) => {
      const x = 20 + i * (280 / (stops.length - 1));
      return <g key={s} data-id="element-857">
            <circle cx={x} cy="22" r="6.5" fill="#FFFFFF" stroke={color} strokeWidth="3" data-id="element-858" />
            <text x={x} y="44" textAnchor="middle" fill={textColor} fontFamily="JetBrains Mono, monospace" fontSize="6.5" letterSpacing="2" fontWeight="700" data-id="element-859">
              {s}
            </text>
          </g>;
    })}
    </svg>;
}
/* ------------------------------------------------------------------ */
/*  CATEGORY HEADER                                                    */
/* ------------------------------------------------------------------ */
function CategoryHeader({
  number,
  title,
  count,
  desc
}: {
  number: string;
  title: string;
  count: string;
  desc: string;
}) {
  return <div className="flex items-end justify-between mb-6 pb-3 border-b border-[var(--divider)]" data-id="element-860">
      <div data-id="element-861">
        <div className="font-mono text-[10px] text-[var(--accent-blue)] tracking-widest mb-1" data-id="element-862">
          {number} · {count}
        </div>
        <div className="font-space font-bold text-3xl tracking-tight leading-tight" data-id="element-863">
          {title}
        </div>
        <div className="text-[12px] text-[var(--text-muted)] leading-relaxed mt-1 max-w-md" data-id="element-864">
          {desc}
        </div>
      </div>
      <div className="font-mono text-[10px] text-[var(--text-muted)] tracking-widest hidden md:block" data-id="element-865">
        PRINT FILES · SS26
      </div>
    </div>;
}
/* ================================================================== */
/*  12.1 MECHANIC PATCHES — embroidered patch artwork                 */
/* ================================================================== */
function OvalPatch() {
  return <svg viewBox="0 0 320 180" className="w-full max-w-[360px]" data-id="element-866">
      <defs data-id="element-867">
        <filter id="op-stitch" x="-2%" y="-2%" width="104%" height="104%" data-id="element-868">
          <feGaussianBlur stdDeviation="0.3" data-id="element-869" />
        </filter>
      </defs>
      {/* chain stitch outer */}
      <ellipse cx="160" cy="90" rx="135" ry="72" fill="none" stroke="#FAF4E7" strokeWidth="1" strokeDasharray="2 1.5" opacity="0.5" data-id="element-870" />
      {/* patch body */}
      <ellipse cx="160" cy="90" rx="130" ry="68" fill="#1A2BFF" data-id="element-871" />
      {/* inner stitched ring */}
      <ellipse cx="160" cy="90" rx="118" ry="58" fill="none" stroke="#FAF4E7" strokeWidth="1.2" strokeDasharray="3 2" data-id="element-872" />
      <ellipse cx="160" cy="90" rx="122" ry="61" fill="none" stroke="#0E0E12" strokeOpacity="0.25" strokeWidth="0.5" data-id="element-873" />
      {/* italic wordmark */}
      <text x="160" y="85" textAnchor="middle" fill="#FAF4E7" fontFamily="Space Grotesk, sans-serif" fontSize="42" fontWeight="800" fontStyle="italic" letterSpacing="-2" data-id="element-874">
        metro
      </text>
      <text x="160" y="115" textAnchor="middle" fill="#FAF4E7" fontFamily="JetBrains Mono, monospace" fontSize="9" letterSpacing="6" opacity="0.85" data-id="element-875">
        AGENCY · BCN
      </text>
    </svg>;
}
function NameTapePatch() {
  return <svg viewBox="0 0 320 140" className="w-full max-w-[360px]" data-id="element-876">
      {/* outer shadow */}
      <rect x="10" y="20" width="300" height="100" rx="6" fill="#FAF4E7" stroke="#0E0E12" strokeOpacity="0.15" data-id="element-877" />
      {/* inner stitch */}
      <rect x="18" y="28" width="284" height="84" rx="3" fill="none" stroke="#0E0E12" strokeWidth="1" strokeDasharray="3 2" data-id="element-878" />
      {/* mono code left */}
      <rect x="30" y="42" width="60" height="56" fill="#1A2BFF" data-id="element-879" />
      <text x="60" y="78" textAnchor="middle" fill="#FAF4E7" fontFamily="Space Grotesk, sans-serif" fontSize="22" fontWeight="800" data-id="element-880">
        L-1
      </text>
      {/* right name */}
      <text x="105" y="70" fill="#0E0E12" fontFamily="Space Grotesk, sans-serif" fontSize="26" fontWeight="800" letterSpacing="-0.5" data-id="element-881">
        OPERATOR
      </text>
      <text x="105" y="92" fill="#0E0E12" fontFamily="JetBrains Mono, monospace" fontSize="8" letterSpacing="3" opacity="0.6" data-id="element-882">
        METRO · STUDIO · EST 2018
      </text>
    </svg>;
}
function CircleBadgePatch() {
  return <svg viewBox="0 0 220 220" className="w-full max-w-[280px]" data-id="element-883">
      <defs data-id="element-884">
        <path id="cb-top" d="M 110,110 m -82,0 a 82,82 0 0,1 164,0" data-id="element-885" />
        <path id="cb-bot" d="M 110,110 m -82,0 a 82,82 0 1,0 164,0" transform="rotate(180 110 110)" data-id="element-886" />
      </defs>
      {/* outer chain stitch */}
      <circle cx="110" cy="110" r="98" fill="none" stroke="#F26A35" strokeWidth="1" strokeDasharray="2 1.5" opacity="0.7" data-id="element-887" />
      {/* body */}
      <circle cx="110" cy="110" r="92" fill="#F26A35" data-id="element-888" />
      {/* inner ring */}
      <circle cx="110" cy="110" r="80" fill="none" stroke="#0E0E12" strokeWidth="1.5" strokeDasharray="3 2" data-id="element-889" />
      <circle cx="110" cy="110" r="84" fill="none" stroke="#0E0E12" strokeOpacity="0.3" strokeWidth="0.5" data-id="element-890" />
      {/* arched top */}
      <text fill="#0E0E12" fontFamily="Space Grotesk, sans-serif" fontSize="14" fontWeight="800" letterSpacing="4" data-id="element-891">
        <textPath href="#cb-top" startOffset="50%" textAnchor="middle" data-id="element-892">
          METRO AGENCY
        </textPath>
      </text>
      <text fill="#0E0E12" fontFamily="JetBrains Mono, monospace" fontSize="9" letterSpacing="3" opacity="0.7" data-id="element-893">
        <textPath href="#cb-bot" startOffset="50%" textAnchor="middle" data-id="element-894">
          BARCELONA · SS · 26
        </textPath>
      </text>
      {/* center M */}
      <text x="110" y="112" textAnchor="middle" fill="#0E0E12" fontFamily="Space Grotesk, sans-serif" fontSize="60" fontWeight="800" fontStyle="italic" letterSpacing="-3" data-id="element-895">
        M
      </text>
      {/* star */}
      <text x="110" y="148" textAnchor="middle" fill="#0E0E12" fontSize="14" data-id="element-896">
        ★
      </text>
    </svg>;
}
function ShieldPatch() {
  return <svg viewBox="0 0 220 240" className="w-full max-w-[260px]" data-id="element-897">
      {/* outer stitch */}
      <path d="M40 30 L180 30 L180 130 Q180 200 110 220 Q40 200 40 130 Z" fill="none" stroke="#FAF4E7" strokeWidth="1" strokeDasharray="2 1.5" opacity="0.5" data-id="element-898" />
      {/* shield body */}
      <path d="M44 34 L176 34 L176 130 Q176 196 110 214 Q44 196 44 130 Z" fill="#0E0E12" data-id="element-899" />
      {/* inner stitch */}
      <path d="M54 44 L166 44 L166 128 Q166 188 110 204 Q54 188 54 128 Z" fill="none" stroke="#FAF4E7" strokeWidth="1" strokeDasharray="3 2" opacity="0.85" data-id="element-900" />
      {/* top label band */}
      <rect x="54" y="44" width="112" height="22" fill="#1A2BFF" data-id="element-901" />
      <text x="110" y="60" textAnchor="middle" fill="#FAF4E7" fontFamily="JetBrains Mono, monospace" fontSize="10" letterSpacing="4" fontWeight="700" data-id="element-902">
        STUDIO CREW
      </text>
      {/* M monogram */}
      <text x="110" y="138" textAnchor="middle" fill="#FAF4E7" fontFamily="Space Grotesk, sans-serif" fontSize="62" fontWeight="800" fontStyle="italic" letterSpacing="-4" data-id="element-903">
        M
      </text>
      {/* two lines */}
      <line x1="68" y1="160" x2="152" y2="160" stroke="#1A2BFF" strokeWidth="3" data-id="element-904" />
      <line x1="76" y1="170" x2="144" y2="170" stroke="#F26A35" strokeWidth="3" data-id="element-905" />
      {/* footer */}
      <text x="110" y="190" textAnchor="middle" fill="#FAF4E7" fontFamily="JetBrains Mono, monospace" fontSize="7.5" letterSpacing="3" opacity="0.8" data-id="element-906">
        BCN · EST 2018
      </text>
    </svg>;
}
/* ================================================================== */
/*  12.2 TEE PRINTS                                                    */
/* ================================================================== */
function TeePrintLogoStack() {
  return <div className="flex flex-col items-center gap-4 w-full" data-id="element-907">
      <MetroLogoMark height={68} data-id="element-908" />
      <div className="w-32 h-px bg-[#1A2BFF]" data-id="element-909" />
      <div className="font-mono text-[8px] tracking-[0.4em] text-[#0E0E12] opacity-70 text-center" data-id="element-910">
        BRAND · WEB · CONTENT
        <br data-id="element-911" />
        MARKETING · AI
      </div>
      <div className="font-mono text-[7px] tracking-[0.35em] text-[#1A2BFF] mt-1" data-id="element-912">
        BARCELONA · SS 26
      </div>
    </div>;
}
function TeePrintRouteMap() {
  return <div className="w-full flex flex-col items-center gap-5" data-id="element-913">
      <div className="font-mono text-[8px] tracking-[0.4em] text-white/70" data-id="element-914">
        LA RED METRO · LÍNEA M
      </div>
      <RouteMapHorizontal color="#F26A35" textColor="#FFFFFF" stops={['BRAND', 'WEB', 'CONTENT', 'MKT', 'AI']} data-id="element-915" />
      <div className="font-space font-extrabold italic text-3xl tracking-tight text-white -mt-1" data-id="element-916">
        all stops served.
      </div>
    </div>;
}
function TeePrintBigType() {
  return <div className="w-full text-center" data-id="element-917">
      <div className="flex items-center justify-center gap-2 mb-3" data-id="element-918">
        <LineBullet label="L1" bg="#1A2BFF" fg="#FFFFFF" size={22} data-id="element-919" />
        <LineBullet label="L2" bg="#0E0E12" fg="#FFFFFF" size={22} data-id="element-920" />
        <LineBullet label="L3" bg="#F26A35" fg="#FFFFFF" size={22} data-id="element-921" />
      </div>
      <div className="font-space font-extrabold leading-[0.85] tracking-[-0.04em] text-white" style={{
      fontSize: 44
    }} data-id="element-922">
        DONDE
        <br data-id="element-923" />
        <span style={{
        color: '#F26A35'
      }} data-id="element-924">
          LAS IDEAS
        </span>
        <br data-id="element-925" />
        CONECTAN.
      </div>
      <div className="font-mono text-[8px] tracking-[0.4em] text-white/60 mt-4" data-id="element-926">
        — METRO · MANIFESTO · 26
      </div>
    </div>;
}
function TeePrintDepartures() {
  const rows = [{
    t: '09:00',
    d: 'BRAND',
    s: 'L-1',
    c: '#F26A35'
  }, {
    t: '11:00',
    d: 'WEB',
    s: 'L-2',
    c: '#F26A35'
  }, {
    t: '14:00',
    d: 'CONTENT',
    s: 'L-3',
    c: '#F26A35'
  }, {
    t: '16:30',
    d: 'MARKETING',
    s: 'L-4',
    c: '#F26A35'
  }, {
    t: '19:00',
    d: 'AI · NEW',
    s: 'L-5',
    c: '#E8C547'
  }];
  return <div className="w-full font-mono text-white" data-id="element-927">
      <div className="flex items-center justify-between mb-3 pb-2 border-b border-white/30" data-id="element-928">
        <div className="text-[9px] tracking-[0.4em] opacity-70" data-id="element-929">
          DEPARTURES · BCN
        </div>
        <div className="text-[9px] tracking-[0.4em] opacity-70" data-id="element-930">SS 26</div>
      </div>
      <div className="flex flex-col gap-1.5" data-id="element-931">
        {rows.map(r => <div key={r.d} className="flex items-center justify-between gap-3 text-[12px] font-bold tracking-wider" style={{
        color: r.c
      }} data-id="element-932">
            <span data-id="element-933">{r.t}</span>
            <span className="flex-1 border-b border-dotted border-white/25" data-id="element-934" />
            <span data-id="element-935">{r.d}</span>
            <span className="opacity-60 text-[9px]" data-id="element-936">{r.s}</span>
          </div>)}
      </div>
      <div className="mt-3 pt-2 border-t border-white/30 text-[8px] tracking-[0.35em] opacity-60 text-center" data-id="element-937">
        TODAS LAS LÍNEAS · OPERATIVAS
      </div>
    </div>;
}
/* ================================================================== */
/*  12.3 HOODIE BACK-PRINTS                                            */
/* ================================================================== */
function HoodieBackOutline() {
  return <div className="w-full text-center text-white flex flex-col items-center gap-4" data-id="element-938">
      <div className="font-mono text-[8px] tracking-[0.4em] opacity-60" data-id="element-939">
        ESTUDIO METRO · BERLIN ROUTE
      </div>
      <div className="font-space font-extrabold italic leading-[0.85] tracking-[-0.04em] w-full" style={{
      fontSize: 62,
      color: 'transparent',
      WebkitTextStroke: '1.5px #FFFFFF'
    }} data-id="element-940">
        METRO
        <br data-id="element-941" />
        AGENCY
      </div>
      <div className="font-mono text-[8px] tracking-[0.4em] opacity-70 mt-1" data-id="element-942">
        DANGEROUS IDEAS · BCN · 2026
      </div>
      <div className="w-3/4 mt-4" data-id="element-943">
        <RouteMapHorizontal color="#FFFFFF" textColor="#FFFFFF" stops={['L1', 'L2', 'L3', 'L4', 'L5']} data-id="element-944" />
      </div>
    </div>;
}
function HoodieBackRoundel() {
  return <div className="w-full flex flex-col items-center justify-center gap-6" data-id="element-945">
      {/* roundel */}
      <div className="relative" data-id="element-946">
        <div className="rounded-full" style={{
        width: 160,
        height: 160,
        border: '14px solid #1A2BFF'
      }} data-id="element-947" />
        <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 flex items-center justify-center" style={{
        height: 32,
        background: '#1A2BFF'
      }} data-id="element-948">
          <span className="font-space font-extrabold italic text-white text-2xl tracking-tight" data-id="element-949">
            METRO
          </span>
        </div>
      </div>
      <div className="text-center" data-id="element-950">
        <div className="font-space font-extrabold italic text-3xl tracking-tight text-[#0E0E12] leading-none" data-id="element-951">
          ideas in transit.
        </div>
        <div className="font-mono text-[8px] tracking-[0.4em] text-[#0E0E12] opacity-60 mt-2" data-id="element-952">
          BARCELONA · SS · 2026
        </div>
      </div>
    </div>;
}
function HoodieBackManifesto() {
  return <div className="w-full text-white" data-id="element-953">
      <div className="flex items-baseline justify-between mb-4" data-id="element-954">
        <div className="font-mono text-[8px] tracking-[0.4em] opacity-60" data-id="element-955">
          SERVICE NOTICE
        </div>
        <div className="font-mono text-[8px] tracking-[0.4em] opacity-60" data-id="element-956">
          No. 026
        </div>
      </div>
      <div className="font-space font-extrabold leading-[0.95] tracking-[-0.03em]" style={{
      fontSize: 28
    }} data-id="element-957">
        NUNCA
        <br data-id="element-958" />
        DOS PIEZAS
        <br data-id="element-959" />
        <span style={{
        color: '#F26A35'
      }} data-id="element-960">
          IDÉNTICAS.
        </span>
      </div>
      <div className="mt-5 pt-3 border-t border-white/25 flex items-center gap-2" data-id="element-961">
        <LineBullet label="M" bg="#1A2BFF" fg="#FFFFFF" size={22} data-id="element-962" />
        <div className="flex-1" data-id="element-963">
          <div className="font-mono text-[8px] tracking-[0.3em] opacity-70" data-id="element-964">
            METRO · OPERATOR EDITION
          </div>
          <div className="font-mono text-[7px] tracking-[0.3em] opacity-50" data-id="element-965">
            Run of 50. Numbered. Not restocked.
          </div>
        </div>
      </div>
    </div>;
}
/* ================================================================== */
/*  12.4 STICKERS — transit-language die-cuts                          */
/* ================================================================== */
function StickerFrame({
  children,
  rotate = 0,
  shape = 'square'
}: {
  children: React.ReactNode;
  rotate?: number;
  shape?: 'square' | 'circle' | 'rect-h' | 'rect-v' | 'oval' | 'hex' | 'diamond' | 'blade';
}) {
  // dimensions per shape
  const dims: Record<string, {
    w: number;
    h: number;
    clip?: string;
    rounded?: string;
  }> = {
    square: {
      w: 140,
      h: 140,
      rounded: '8px'
    },
    circle: {
      w: 140,
      h: 140,
      rounded: '50%'
    },
    'rect-h': {
      w: 200,
      h: 70,
      rounded: '6px'
    },
    'rect-v': {
      w: 110,
      h: 160,
      rounded: '6px'
    },
    oval: {
      w: 180,
      h: 100,
      rounded: '50%'
    },
    hex: {
      w: 150,
      h: 130,
      clip: 'polygon(50% 0,100% 25%,100% 75%,50% 100%,0 75%,0 25%)'
    },
    diamond: {
      w: 140,
      h: 140,
      clip: 'polygon(50% 0,100% 50%,50% 100%,0 50%)'
    },
    blade: {
      w: 210,
      h: 80,
      clip: 'polygon(0 0, 90% 0, 100% 50%, 90% 100%, 0 100%)'
    }
  };
  const d = dims[shape];
  return <div style={{
    width: d.w,
    height: d.h,
    transform: `rotate(${rotate}deg)`,
    filter: 'drop-shadow(0 8px 18px rgba(0,0,0,0.18))'
  }} data-id="element-966">
      {/* outer white die-cut bleed */}
      <div style={{
      width: '100%',
      height: '100%',
      background: '#FFFFFF',
      borderRadius: d.rounded,
      clipPath: d.clip,
      padding: 6
    }} data-id="element-967">
        {children}
      </div>
    </div>;
}
function StickerLogoSquare() {
  return <StickerFrame shape="square" rotate={-4} data-id="element-968">
      <div className="w-full h-full bg-[#1A2BFF] rounded-[4px] flex flex-col items-center justify-center p-3" data-id="element-969">
        <MetroLogoMark height={36} invert data-id="element-970" />
        <div className="mt-2 font-mono text-[6.5px] tracking-[0.35em] text-white/85" data-id="element-971">
          AGENCY · BCN · 26
        </div>
      </div>
    </StickerFrame>;
}
function StickerLineBullets() {
  return <StickerFrame shape="rect-h" rotate={2} data-id="element-972">
      <div className="w-full h-full bg-[#0E0E12] rounded-[3px] flex items-center justify-center gap-2 px-3" data-id="element-973">
        <LineBullet label="L1" bg="#1A2BFF" fg="#FFFFFF" size={32} data-id="element-974" />
        <LineBullet label="L2" bg="#FFFFFF" fg="#0E0E12" size={32} data-id="element-975" />
        <LineBullet label="L3" bg="#F26A35" fg="#FFFFFF" size={32} data-id="element-976" />
      </div>
    </StickerFrame>;
}
function StickerRoundel() {
  return <StickerFrame shape="circle" rotate={-3} data-id="element-977">
      <div className="w-full h-full bg-white rounded-full flex items-center justify-center relative overflow-hidden" data-id="element-978">
        <div className="rounded-full" style={{
        width: '85%',
        height: '85%',
        border: '12px solid #1A2BFF'
      }} data-id="element-979" />
        <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 flex items-center justify-center" style={{
        height: 26,
        background: '#1A2BFF'
      }} data-id="element-980">
          <span className="font-space font-extrabold italic text-white text-base tracking-tight" data-id="element-981">
            METRO
          </span>
        </div>
      </div>
    </StickerFrame>;
}
function StickerBlade() {
  return <StickerFrame shape="blade" rotate={-2} data-id="element-982">
      <div className="w-full h-full bg-[#E8C547] flex items-center px-4 gap-2" data-id="element-983">
        <span className="font-space font-extrabold text-[#0E0E12] text-2xl" data-id="element-984">
          →
        </span>
        <div className="flex flex-col leading-tight" data-id="element-985">
          <div className="font-mono text-[6.5px] tracking-[0.35em] text-[#0E0E12] opacity-70" data-id="element-986">
            NEXT STOP
          </div>
          <div className="font-space font-extrabold italic text-[#0E0E12] text-xl tracking-tight" data-id="element-987">
            metro · bcn
          </div>
        </div>
      </div>
    </StickerFrame>;
}
function StickerMindTheBrief() {
  return <StickerFrame shape="rect-h" rotate={3} data-id="element-988">
      <div className="w-full h-full bg-[#0E0E12] rounded-[3px] flex items-center justify-center px-4" data-id="element-989">
        <div className="text-center" data-id="element-990">
          <div className="font-mono text-[6.5px] tracking-[0.4em] text-[#F26A35] mb-1" data-id="element-991">
            ⚠ ATTENTION ⚠
          </div>
          <div className="font-space font-extrabold italic text-white text-lg tracking-tight leading-none" data-id="element-992">
            mind the brief
          </div>
          <div className="font-mono text-[6px] tracking-[0.35em] text-white/60 mt-1" data-id="element-993">
            METRO · BCN · 2026
          </div>
        </div>
      </div>
    </StickerFrame>;
}
function StickerFareCard() {
  return <StickerFrame shape="rect-h" rotate={-3} data-id="element-994">
      <div className="w-full h-full bg-[#1A2BFF] rounded-[3px] relative overflow-hidden flex items-center px-3" data-id="element-995">
        {/* magnetic stripe */}
        <div className="absolute left-0 right-0 top-3 h-3 bg-[#0E0E12]" data-id="element-996" />
        <div className="flex items-center justify-between w-full mt-4" data-id="element-997">
          <MetroLogoMark height={24} invert data-id="element-998" />
          <div className="text-right" data-id="element-999">
            <div className="font-mono text-[6.5px] tracking-[0.3em] text-white/75" data-id="element-1000">
              FARE CARD
            </div>
            <div className="font-space font-extrabold italic text-white text-base tracking-tight leading-none" data-id="element-1001">
              unlimited
            </div>
            <div className="font-mono text-[6px] tracking-[0.3em] text-white/60 mt-0.5" data-id="element-1002">
              EXP · 12/26
            </div>
          </div>
        </div>
      </div>
    </StickerFrame>;
}
function StickerHexStation() {
  return <StickerFrame shape="hex" rotate={4} data-id="element-1003">
      <div className="w-full h-full bg-[#F26A35] flex flex-col items-center justify-center" style={{
      clipPath: 'polygon(50% 0,100% 25%,100% 75%,50% 100%,0 75%,0 25%)'
    }} data-id="element-1004">
        <div className="font-mono text-[6.5px] tracking-[0.4em] text-[#0E0E12] opacity-70" data-id="element-1005">
          STATION
        </div>
        <div className="font-space font-extrabold italic text-[#0E0E12] text-3xl tracking-tight leading-none" data-id="element-1006">
          BCN—01
        </div>
        <div className="font-mono text-[6.5px] tracking-[0.4em] text-[#0E0E12] opacity-70 mt-1" data-id="element-1007">
          ESTUDIO METRO
        </div>
      </div>
    </StickerFrame>;
}
function StickerOvalAgency() {
  return <StickerFrame shape="oval" rotate={-5} data-id="element-1008">
      <div className="w-full h-full bg-[#FAF4E7] rounded-full flex flex-col items-center justify-center relative" data-id="element-1009">
        <div className="absolute inset-2 rounded-full border border-[#1A2BFF]/40" data-id="element-1010" />
        <div className="font-mono text-[6px] tracking-[0.4em] text-[#1A2BFF]" data-id="element-1011">
          ★ OFFICIAL ★
        </div>
        <div className="font-space font-extrabold italic text-[#1A2BFF] text-2xl tracking-tight leading-none mt-0.5" data-id="element-1012">
          metro
        </div>
        <div className="font-mono text-[6px] tracking-[0.4em] text-[#1A2BFF] mt-1 opacity-75" data-id="element-1013">
          BARCELONA · 2018
        </div>
      </div>
    </StickerFrame>;
}
/* ================================================================== */
/*  12.5 TOTE PRINTS                                                   */
/* ================================================================== */
function TotePrintLogo() {
  return <div className="flex flex-col items-center gap-6 w-full" data-id="element-1014">
      <MetroLogoMark height={90} data-id="element-1015" />
      <div className="w-40 h-px bg-[#0E0E12]/30" data-id="element-1016" />
      <div className="font-mono text-[8px] tracking-[0.45em] text-[#0E0E12]/65 text-center" data-id="element-1017">
        AGENCIA DE COMUNICACIÓN
        <br data-id="element-1018" />
        BARCELONA · DESDE 2018
      </div>
    </div>;
}
function TotePrintMap() {
  // a more developed transit map
  const dots = [{
    x: 20,
    y: 40,
    c: '#1A2BFF',
    label: 'BRAND'
  }, {
    x: 70,
    y: 40,
    c: '#1A2BFF',
    label: 'WEB'
  }, {
    x: 140,
    y: 40,
    c: '#1A2BFF',
    label: 'CONTENT'
  }, {
    x: 200,
    y: 40,
    c: '#1A2BFF',
    label: 'MKT'
  }, {
    x: 260,
    y: 40,
    c: '#1A2BFF',
    label: 'AI'
  }];
  return <div className="w-full flex flex-col gap-3" data-id="element-1019">
      <div className="font-mono text-[7px] tracking-[0.4em] text-white/65" data-id="element-1020">
        LA RED · METRO · BCN
      </div>
      <svg viewBox="0 0 280 240" className="w-full" data-id="element-1021">
        {/* L-1 horizontal */}
        <line x1="20" y1="50" x2="260" y2="50" stroke="#1A2BFF" strokeWidth="4" data-id="element-1022" />
        {/* L-2 diagonal */}
        <line x1="40" y1="50" x2="200" y2="200" stroke="#F26A35" strokeWidth="4" data-id="element-1023" />
        {/* L-3 vertical */}
        <line x1="140" y1="20" x2="140" y2="220" stroke="#FFFFFF" strokeWidth="4" data-id="element-1024" />
        {/* L-4 curve */}
        <path d="M260 50 Q 260 150 140 220" stroke="#E8C547" strokeWidth="4" fill="none" data-id="element-1025" />

        {/* stations on L-1 */}
        {dots.map(d => <circle key={d.label} cx={d.x} cy="50" r="5" fill="#0E0E12" stroke="#FFFFFF" strokeWidth="2" data-id="element-1026" />)}
        {/* labels */}
        {dots.map(d => <text key={`l-${d.label}`} x={d.x} y="36" textAnchor="middle" fill="#FFFFFF" fontFamily="JetBrains Mono, monospace" fontSize="6.5" letterSpacing="1.5" data-id="element-1027">
            {d.label}
          </text>)}
        {/* interchange */}
        <circle cx="140" cy="50" r="10" fill="none" stroke="#FFFFFF" strokeWidth="2" data-id="element-1028" />
        {/* extra stations */}
        <circle cx="200" cy="200" r="5" fill="#0E0E12" stroke="#FFFFFF" strokeWidth="2" data-id="element-1029" />
        <text x="200" y="215" textAnchor="middle" fill="#FFFFFF" fontFamily="JetBrains Mono, monospace" fontSize="6.5" letterSpacing="1.5" data-id="element-1030">
          OPS
        </text>
        <circle cx="140" cy="220" r="5" fill="#0E0E12" stroke="#FFFFFF" strokeWidth="2" data-id="element-1031" />
        <text x="140" y="234" textAnchor="middle" fill="#FFFFFF" fontFamily="JetBrains Mono, monospace" fontSize="6.5" letterSpacing="1.5" data-id="element-1032">
          BCN—01
        </text>
        {/* line legend */}
        <g transform="translate(20,210)" data-id="element-1033">
          {[{
          c: '#1A2BFF',
          l: 'L-1 BRAND'
        }, {
          c: '#F26A35',
          l: 'L-2 CREATIVE'
        }, {
          c: '#FFFFFF',
          l: 'L-3 CONTENT'
        }, {
          c: '#E8C547',
          l: 'L-4 AI'
        }].map((row, i) => <g key={row.l} transform={`translate(0, ${i * 10})`} data-id="element-1034">
              <rect width="12" height="3" fill={row.c} y="2" data-id="element-1035" />
              <text x="18" y="6" fill="#FFFFFF" fontFamily="JetBrains Mono, monospace" fontSize="5.5" letterSpacing="1" data-id="element-1036">
                {row.l}
              </text>
            </g>)}
        </g>
      </svg>
    </div>;
}
function TotePrintDepartures() {
  const rows = [{
    t: '09:00',
    d: 'BRAND',
    s: 'ON TIME'
  }, {
    t: '11:00',
    d: 'WEB',
    s: 'ON TIME'
  }, {
    t: '14:00',
    d: 'CONTENT',
    s: 'ON TIME'
  }, {
    t: '16:30',
    d: 'MARKETING',
    s: 'BOARDING'
  }, {
    t: '19:00',
    d: 'AI · NEW',
    s: 'NEW'
  }];
  return <div className="w-full font-mono text-[#E8C547]" data-id="element-1037">
      <div className="flex items-center justify-between mb-2 pb-1.5 border-b border-[#E8C547]/40" data-id="element-1038">
        <div className="text-[7px] tracking-[0.4em]" data-id="element-1039">DEPARTURES</div>
        <div className="text-[7px] tracking-[0.4em]" data-id="element-1040">BCN · SS26</div>
      </div>
      <div className="flex flex-col gap-1" data-id="element-1041">
        {rows.map(r => <div key={r.d} className="flex items-center justify-between text-[9px] font-bold tracking-wider" data-id="element-1042">
            <span data-id="element-1043">{r.t}</span>
            <span className="flex-1 mx-2 border-b border-dotted border-[#E8C547]/30" data-id="element-1044" />
            <span data-id="element-1045">{r.d}</span>
            <span className="ml-2 opacity-70 text-[7px]" data-id="element-1046">· {r.s}</span>
          </div>)}
      </div>
      <div className="mt-2 pt-1.5 border-t border-[#E8C547]/40 text-center text-[7px] tracking-[0.35em] opacity-70" data-id="element-1047">
        ALL LINES OPERATIVE
      </div>
    </div>;
}
function TotePrintMonogram() {
  return <div className="w-full h-full flex flex-col items-center justify-center relative" data-id="element-1048">
      <div className="font-space font-extrabold italic leading-[0.8]" style={{
      fontSize: 200,
      letterSpacing: '-0.1em',
      color: '#1A2BFF'
    }} data-id="element-1049">
        M
      </div>
      <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between" data-id="element-1050">
        <div className="font-mono text-[7px] tracking-[0.4em] text-[#0E0E12]/70" data-id="element-1051">
          ME—TRO
        </div>
        <div className="font-mono text-[7px] tracking-[0.4em] text-[#0E0E12]/70" data-id="element-1052">
          BCN · 26
        </div>
      </div>
    </div>;
}
/* ================================================================== */
/*  12.6 BUSINESS CARDS                                                */
/* ================================================================== */
function CardParisTicket() {
  // Two-up: front (top) + back (bottom). Real T+ ratio ~2.17:1
  return <div className="flex flex-col gap-3 w-full max-w-[360px]" data-id="element-1053">
      {/* FRONT */}
      <div className="relative" style={{
      aspectRatio: '2.17 / 1'
    }} data-id="element-1054">
        <svg viewBox="0 0 434 200" className="w-full h-full" style={{
        filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.12))'
      }} data-id="element-1055">
          {/* paper */}
          <rect x="0" y="0" width="434" height="200" fill="#F4ECD8" data-id="element-1056" />
          {/* paper grain — subtle dot pattern */}
          <g opacity="0.06" data-id="element-1057">
            {Array.from({
            length: 30
          }).map((_, i) => Array.from({
            length: 12
          }).map((_, j) => <circle key={`${i}-${j}`} cx={i * 15 + j % 2 * 7} cy={j * 17 + 5} r="0.6" fill="#1A2BFF" data-id="element-1058" />))}
          </g>
          {/* magnetic stripe — brown sepia on right edge */}
          <rect x="380" y="10" width="44" height="180" fill="#7A5638" data-id="element-1059" />
          <rect x="383" y="14" width="38" height="172" fill="#6B4A2E" data-id="element-1060" />
          {/* arrow chevron — insertion indicator */}
          <path d="M 18 100 L 38 80 L 38 90 L 60 90 L 60 110 L 38 110 L 38 120 Z" fill="#1A2BFF" data-id="element-1061" />
          {/* italic wordmark */}
          <text x="80" y="60" fill="#1A2BFF" fontFamily="Space Grotesk, sans-serif" fontSize="34" fontWeight="800" fontStyle="italic" letterSpacing="-1.5" data-id="element-1062">
            metro
          </text>
          {/* M+ ticket type */}
          <g transform="translate(80, 80)" data-id="element-1063">
            <rect width="22" height="22" fill="#1A2BFF" data-id="element-1064" />
            <text x="11" y="17" textAnchor="middle" fill="#F4ECD8" fontFamily="Space Grotesk, sans-serif" fontSize="14" fontWeight="800" data-id="element-1065">
              M+
            </text>
          </g>
          <text x="110" y="96" fill="#1A2BFF" fontFamily="JetBrains Mono, monospace" fontSize="9" letterSpacing="3" fontWeight="700" data-id="element-1066">
            BILLETE · AGENCIA
          </text>
          {/* contact area */}
          <g transform="translate(80, 120)" data-id="element-1067">
            <text fill="#1A2BFF" fontFamily="Space Grotesk, sans-serif" fontSize="13" fontWeight="700" data-id="element-1068">
              Marc Estudio
            </text>
            <text y="14" fill="#1A2BFF" fontFamily="JetBrains Mono, monospace" fontSize="7.5" letterSpacing="1.5" opacity="0.8" data-id="element-1069">
              DIRECTOR CREATIVO
            </text>
            <text y="30" fill="#1A2BFF" fontFamily="JetBrains Mono, monospace" fontSize="7.5" letterSpacing="1.5" data-id="element-1070">
              marc@metroagency.co
            </text>
            <text y="44" fill="#1A2BFF" fontFamily="JetBrains Mono, monospace" fontSize="7" letterSpacing="1.5" opacity="0.7" data-id="element-1071">
              +34 600 000 000
            </text>
          </g>
          {/* corner serials */}
          <text x="18" y="20" fill="#1A2BFF" fontFamily="JetBrains Mono, monospace" fontSize="6" letterSpacing="2" opacity="0.55" data-id="element-1072">
            026 · BCN
          </text>
          <text x="18" y="190" fill="#1A2BFF" fontFamily="JetBrains Mono, monospace" fontSize="6" letterSpacing="2" opacity="0.55" data-id="element-1073">
            VÁLIDO INDEFINIDO
          </text>
          {/* edge perforation hint */}
          {Array.from({
          length: 22
        }).map((_, i) => <circle key={i} cx={5 + i * 17} cy="3" r="0.8" fill="#F4ECD8" stroke="#1A2BFF" strokeOpacity="0.2" strokeWidth="0.4" data-id="element-1074" />)}
        </svg>
      </div>
      {/* BACK */}
      <div className="relative" style={{
      aspectRatio: '2.17 / 1'
    }} data-id="element-1075">
        <svg viewBox="0 0 434 200" className="w-full h-full" style={{
        filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.12))'
      }} data-id="element-1076">
          <rect x="0" y="0" width="434" height="200" fill="#F4ECD8" data-id="element-1077" />
          {/* italic manifesto */}
          <text x="217" y="95" textAnchor="middle" fill="#1A2BFF" fontFamily="Space Grotesk, sans-serif" fontSize="28" fontWeight="800" fontStyle="italic" letterSpacing="-1" data-id="element-1078">
            donde las ideas
          </text>
          <text x="217" y="125" textAnchor="middle" fill="#1A2BFF" fontFamily="Space Grotesk, sans-serif" fontSize="28" fontWeight="800" fontStyle="italic" letterSpacing="-1" data-id="element-1079">
            conectan.
          </text>
          {/* fine print */}
          <text x="217" y="160" textAnchor="middle" fill="#1A2BFF" fontFamily="JetBrains Mono, monospace" fontSize="6" letterSpacing="2" opacity="0.55" data-id="element-1080">
            CONSÉRVESE HASTA LA SALIDA · NO REEMBOLSABLE
          </text>
          <text x="217" y="172" textAnchor="middle" fill="#1A2BFF" fontFamily="JetBrains Mono, monospace" fontSize="6" letterSpacing="2" opacity="0.55" data-id="element-1081">
            METROAGENCY.CO · BARCELONA · 2018—26
          </text>
        </svg>
      </div>
    </div>;
}
function CardWagon() {
  return <svg viewBox="0 0 520 200" className="w-full max-w-[440px]" style={{
    filter: 'drop-shadow(0 6px 14px rgba(0,0,0,0.15))'
  }} data-id="element-1082">
      {/* rails */}
      <line x1="10" y1="185" x2="510" y2="185" stroke="#0E0E12" strokeWidth="1.5" opacity="0.5" data-id="element-1083" />
      <line x1="10" y1="190" x2="510" y2="190" stroke="#0E0E12" strokeWidth="1.5" opacity="0.5" data-id="element-1084" />
      {/* ties */}
      {Array.from({
      length: 14
    }).map((_, i) => <line key={i} x1={20 + i * 36} y1="183" x2={20 + i * 36} y2="192" stroke="#0E0E12" strokeWidth="1" opacity="0.35" data-id="element-1085" />)}
      {/* wagon body */}
      <path d="M 20 180 L 20 50 Q 20 18 60 18 L 460 18 Q 500 18 500 50 L 500 180 Z" fill="#1A2BFF" data-id="element-1086" />
      {/* roof highlight */}
      <path d="M 20 50 Q 20 18 60 18 L 460 18 Q 500 18 500 50" fill="none" stroke="#FFFFFF" strokeOpacity="0.25" strokeWidth="1" data-id="element-1087" />
      {/* destination roller above windows */}
      <rect x="40" y="28" width="440" height="14" fill="#0E0E12" data-id="element-1088" />
      <text x="260" y="38" textAnchor="middle" fill="#E8C547" fontFamily="JetBrains Mono, monospace" fontSize="8" letterSpacing="3" fontWeight="700" data-id="element-1089">
        LÍNEA M · METRO AGENCY · BARCELONA
      </text>
      {/* window row */}
      {[55, 145, 235, 325, 415].map((x, i) => <rect key={i} x={x} y="50" width="60" height="50" fill="#FAF4E7" stroke="#0E0E12" strokeOpacity="0.5" strokeWidth="0.8" data-id="element-1090" />)}
      {/* contact info "inside" the wagon below windows */}
      <g transform="translate(40, 115)" data-id="element-1091">
        <text fill="#FFFFFF" fontFamily="Space Grotesk, sans-serif" fontSize="18" fontWeight="800" fontStyle="italic" letterSpacing="-0.5" data-id="element-1092">
          Marc Estudio
        </text>
        <text y="16" fill="#FFFFFF" fontFamily="JetBrains Mono, monospace" fontSize="7.5" letterSpacing="2" opacity="0.75" data-id="element-1093">
          DIRECTOR CREATIVO · L-1 OPERATOR
        </text>
        <text y="38" fill="#E8C547" fontFamily="JetBrains Mono, monospace" fontSize="8" letterSpacing="2" data-id="element-1094">
          marc@metroagency.co · +34 600 000 000
        </text>
      </g>
      {/* door split lines */}
      <line x1="175" y1="50" x2="175" y2="170" stroke="#0E0E12" strokeOpacity="0.45" strokeWidth="1" data-id="element-1095" />
      <line x1="345" y1="50" x2="345" y2="170" stroke="#0E0E12" strokeOpacity="0.45" strokeWidth="1" data-id="element-1096" />
      {/* car number plate */}
      <rect x="460" y="160" width="30" height="14" fill="#FAF4E7" data-id="element-1097" />
      <text x="475" y="170" textAnchor="middle" fill="#1A2BFF" fontFamily="JetBrains Mono, monospace" fontSize="7" fontWeight="800" letterSpacing="1" data-id="element-1098">
        026
      </text>
      {/* wheels */}
      <circle cx="80" cy="180" r="8" fill="#0E0E12" data-id="element-1099" />
      <circle cx="80" cy="180" r="3" fill="#FAF4E7" data-id="element-1100" />
      <circle cx="440" cy="180" r="8" fill="#0E0E12" data-id="element-1101" />
      <circle cx="440" cy="180" r="3" fill="#FAF4E7" data-id="element-1102" />
    </svg>;
}
function CardVerticalItalic() {
  return <div className="relative bg-[#1A2BFF] overflow-hidden" style={{
    width: 210,
    aspectRatio: '0.6 / 1',
    boxShadow: '0 6px 16px rgba(0,0,0,0.18)'
  }} data-id="element-1103">
      {/* registration marks */}
      <div className="absolute top-3 left-3 right-3 flex items-center justify-between" data-id="element-1104">
        <span className="font-mono text-[7px] tracking-[0.3em] text-white/65" data-id="element-1105">
          METRO
        </span>
        <span className="font-mono text-[7px] tracking-[0.3em] text-white/65" data-id="element-1106">
          BCN · 26
        </span>
      </div>
      {/* giant italic */}
      <div className="absolute font-space font-extrabold italic text-white leading-[0.78] tracking-[-0.08em]" style={{
      fontSize: 120,
      left: -10,
      top: 30
    }} data-id="element-1107">
        me—
        <br data-id="element-1108" />
        tro
      </div>
      {/* bottom contact stack */}
      <div className="absolute bottom-4 left-4 right-4" data-id="element-1109">
        <div className="h-px bg-white/40 mb-2" data-id="element-1110" />
        <div className="font-space font-bold text-white text-sm tracking-tight leading-tight" data-id="element-1111">
          Marc Estudio
        </div>
        <div className="font-mono text-[7px] text-white/75 tracking-[0.2em] mt-0.5" data-id="element-1112">
          DIRECTOR CREATIVO
        </div>
        <div className="font-mono text-[7px] text-[#E8C547] tracking-[0.2em] mt-2" data-id="element-1113">
          marc@metroagency.co
        </div>
      </div>
    </div>;
}
function CardFare() {
  // CR80: 85.6 x 53.98 mm → 1.586:1
  return <div className="flex flex-col gap-3 w-full max-w-[360px]" data-id="element-1114">
      {/* FRONT */}
      <div className="relative bg-[#1A2BFF] overflow-hidden" style={{
      aspectRatio: '1.586 / 1',
      borderRadius: 8,
      boxShadow: '0 4px 14px rgba(0,0,0,0.18)'
    }} data-id="element-1115">
        {/* mag stripe across top */}
        <div className="absolute top-3 left-0 right-0 h-4 bg-[#0E0E12]" data-id="element-1116" />
        <div className="absolute top-3 left-0 right-0 h-4 bg-gradient-to-r from-[#0E0E12] via-[#2a2a30] to-[#0E0E12] opacity-90" data-id="element-1117" />
        {/* chip rectangle */}
        <div className="absolute top-12 left-4 w-9 h-7 rounded-sm bg-[#E8C547] flex items-center justify-center" data-id="element-1118">
          <div className="grid grid-cols-3 gap-px w-6 h-4" data-id="element-1119">
            {Array.from({
            length: 9
          }).map((_, i) => <div key={i} className="bg-[#0E0E12]/60" data-id="element-1120" />)}
          </div>
        </div>
        {/* logo top right */}
        <div className="absolute top-10 right-4" data-id="element-1121">
          <MetroLogoMark height={20} invert data-id="element-1122" />
        </div>
        {/* contact stack */}
        <div className="absolute bottom-3 left-4 right-4" data-id="element-1123">
          <div className="font-mono text-[6.5px] tracking-[0.3em] text-white/65" data-id="element-1124">
            MEMBER · OPERATOR
          </div>
          <div className="font-space font-extrabold italic text-white text-lg tracking-tight leading-none mt-1" data-id="element-1125">
            Marc Estudio
          </div>
          <div className="flex items-end justify-between mt-1.5" data-id="element-1126">
            <div className="font-mono text-[7px] tracking-[0.25em] text-white/80" data-id="element-1127">
              marc@metroagency.co
            </div>
            <div className="font-mono text-[6.5px] tracking-[0.3em] text-[#E8C547]" data-id="element-1128">
              SINCE · 18
            </div>
          </div>
        </div>
      </div>
      {/* BACK */}
      <div className="relative bg-[#FAF4E7] overflow-hidden" style={{
      aspectRatio: '1.586 / 1',
      borderRadius: 8,
      boxShadow: '0 4px 14px rgba(0,0,0,0.18)'
    }} data-id="element-1129">
        {/* signature strip */}
        <div className="absolute top-6 left-3 right-3 h-6 bg-white border border-[#1A2BFF]/20" data-id="element-1130" />
        <div className="absolute top-8 left-4 font-mono text-[6px] tracking-[0.3em] text-[#1A2BFF]/55" data-id="element-1131">
          FIRMA / SIGNATURE
        </div>
        <div className="absolute bottom-3 left-3 right-3" data-id="element-1132">
          <div className="h-px bg-[#1A2BFF]/30 mb-1.5" data-id="element-1133" />
          <div className="font-mono text-[6px] tracking-[0.3em] text-[#1A2BFF]/70 leading-relaxed" data-id="element-1134">
            METRO AGENCY · BARCELONA · METROAGENCY.CO
            <br data-id="element-1135" />
            VÁLIDO EN TODAS LAS LÍNEAS DEL SISTEMA · NO TRANSFERIBLE
          </div>
        </div>
      </div>
    </div>;
}
function CardRoundel() {
  return <div className="relative bg-[#FAF4E7] overflow-hidden" style={{
    width: 280,
    aspectRatio: '1 / 1',
    boxShadow: '0 6px 16px rgba(0,0,0,0.15)'
  }} data-id="element-1136">
      {/* top mono */}
      <div className="absolute top-3 left-3 right-3 flex items-center justify-between" data-id="element-1137">
        <span className="font-mono text-[7px] tracking-[0.35em] text-[#1A2BFF]/65" data-id="element-1138">
          METRO · BCN
        </span>
        <span className="font-mono text-[7px] tracking-[0.35em] text-[#1A2BFF]/65" data-id="element-1139">
          No. 026
        </span>
      </div>
      {/* roundel */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-[60%] flex items-center justify-center" data-id="element-1140">
        <div className="relative" data-id="element-1141">
          <div className="rounded-full" style={{
          width: 140,
          height: 140,
          border: '14px solid #1A2BFF'
        }} data-id="element-1142" />
          <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 flex items-center justify-center" style={{
          height: 28,
          background: '#1A2BFF'
        }} data-id="element-1143">
            <span className="font-space font-extrabold italic text-white text-xl tracking-tight" data-id="element-1144">
              METRO
            </span>
          </div>
        </div>
      </div>
      {/* bottom contact */}
      <div className="absolute bottom-3 left-3 right-3 text-center" data-id="element-1145">
        <div className="font-space font-bold text-[#0E0E12] text-sm tracking-tight leading-tight" data-id="element-1146">
          Marc Estudio
        </div>
        <div className="font-mono text-[7px] text-[#0E0E12]/65 tracking-[0.25em] mt-0.5" data-id="element-1147">
          DIRECTOR CREATIVO
        </div>
        <div className="font-mono text-[7px] text-[#1A2BFF] tracking-[0.25em] mt-1.5" data-id="element-1148">
          marc@metroagency.co
        </div>
      </div>
    </div>;
}
function CardDepartures() {
  return <div className="relative bg-[#0E0E12] overflow-hidden" style={{
    width: 360,
    aspectRatio: '1.7 / 1',
    boxShadow: '0 6px 16px rgba(0,0,0,0.18)'
  }} data-id="element-1149">
      {/* header bar */}
      <div className="flex items-center justify-between px-3 pt-3 pb-2 border-b border-[#E8C547]/30" data-id="element-1150">
        <span className="font-mono text-[7px] tracking-[0.4em] text-[#E8C547]" data-id="element-1151">
          DEPARTURES
        </span>
        <span className="font-mono text-[7px] tracking-[0.4em] text-[#E8C547]/70" data-id="element-1152">
          BCN · GATE 01
        </span>
      </div>
      {/* row — name treated as a departure */}
      <div className="px-3 py-2 flex items-center gap-2 font-mono" data-id="element-1153">
        <span className="text-[#F26A35] text-[10px] tracking-wider font-bold" data-id="element-1154">
          09:00
        </span>
        <span className="flex-1 border-b border-dotted border-[#E8C547]/30" data-id="element-1155" />
        <span className="text-[#F26A35] text-[12px] tracking-wider font-bold" data-id="element-1156">
          MARC ESTUDIO
        </span>
      </div>
      <div className="px-3 flex items-center justify-between font-mono text-[#E8C547]/80 text-[7px] tracking-[0.3em]" data-id="element-1157">
        <span data-id="element-1158">DESTINO · DIRECCIÓN CREATIVA</span>
        <span data-id="element-1159">PLAT · L-1</span>
      </div>
      {/* footer dot-matrix */}
      <div className="absolute bottom-2 left-3 right-3 flex items-center justify-between font-mono text-[6.5px] tracking-[0.35em]" data-id="element-1160">
        <span className="text-[#E8C547]/60" data-id="element-1161">marc@metroagency.co</span>
        <span className="text-[#F26A35]" data-id="element-1162">● ON TIME</span>
      </div>
      {/* dot-matrix subtle texture */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.05]" style={{
      backgroundImage: 'radial-gradient(circle, #E8C547 0.5px, transparent 0.5px)',
      backgroundSize: '4px 4px'
    }} data-id="element-1163" />
    </div>;
}
/* ================================================================== */
/*  MAIN SECTION                                                       */
/* ================================================================== */
export function GoodiesSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <section id="goodies" ref={ref} className="min-h-screen px-8 py-24" data-id="element-1164">
      <motion.div initial={{
      opacity: 0,
      y: 40
    }} animate={isInView ? {
      opacity: 1,
      y: 0
    } : {}} transition={{
      duration: 0.5,
      ease: 'easeOut'
    }} className="max-w-7xl mx-auto" data-id="element-1165">
        {/* Section header */}
        <div className="mb-12 max-w-3xl" data-id="element-1166">
          <div className="font-mono text-xs text-[var(--text-muted)] tracking-wider mb-4" data-id="element-1167">
            12 / GOODIES · PRINT FILES
          </div>
          <h2 className="font-space text-5xl md:text-6xl font-medium tracking-tight" data-id="element-1168">
            La marca, en formato impreso.
          </h2>
          <p className="text-lg text-[var(--text-muted)] mt-4 font-helvetica leading-relaxed" data-id="element-1169">
            Esta sección presenta el archivo gráfico, no la prenda. Cada artwork
            está dibujado sobre lienzo plano, listo para producción. El
            vocabulario es transit — parches bordados, bullets de línea,
            roundels, mapas de red, blades de andén y tarjetas de tarifa —
            cocinado con el sistema Metro: italic chunky, mono, líneas de color.
          </p>
        </div>

        {/* Production strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-16" data-id="element-1170">
          {[{
          t: 'Sistema',
          v: 'Transit-inspired',
          d: 'NYC · London · Tokyo · BCN'
        }, {
          t: 'Tipografía',
          v: 'Space Grotesk + Mono',
          d: 'Italic chunky + technical mono'
        }, {
          t: 'Tintas base',
          v: 'Blue · Ink · Orange',
          d: '+ Cream para textiles ink'
        }, {
          t: 'Formato',
          v: 'Archivo plano',
          d: 'Vector listo para serigrafía / DTF'
        }].map(c => <div key={c.t} className="border border-[var(--divider)] rounded-sm p-4 bg-[var(--bg-surface)]" data-id="element-1171">
              <div className="font-mono text-[10px] text-[var(--text-muted)] tracking-widest mb-2" data-id="element-1172">
                {c.t.toUpperCase()}
              </div>
              <div className="font-space font-bold text-base leading-tight mb-1" data-id="element-1173">
                {c.v}
              </div>
              <div className="text-[10px] text-[var(--text-muted)] leading-relaxed" data-id="element-1174">
                {c.d}
              </div>
            </div>)}
        </div>

        {/* ============================================================ */}
        {/* 12.1 — MECHANIC PATCHES                                      */}
        {/* ============================================================ */}
        <CategoryHeader number="12.1" title="Mechanic Patches." count="4 patch artworks" desc="Parches bordados para camisas mecánico tipo workwear. Cuatro formas clásicas — óvalo, name-tape, badge circular y escudo — cada una en una familia de color distinta. La prenda es el soporte; estos son los archivos." data-id="element-1175" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-20" data-id="element-1176">
          <ItemCard number="MS · 01" title="Oval Patch" meta="BORDADO · CHAIN STITCH" caption="El óvalo clásico. Italic chunky con borde de cadeneta, base azul Metro sobre fondo cream." inks={['#1A2BFF', '#FAF4E7']} data-id="element-1177">
            <PrintCanvas bg="#F4F0E8" data-id="element-1178">
              <OvalPatch data-id="element-1179" />
            </PrintCanvas>
          </ItemCard>
          <ItemCard number="MS · 02" title="Name Tape" meta="RECTÁNGULO · BORDADO" caption="Inspirado en uniformes de operario. Bloque azul con código de línea + nombre del rol en bold." inks={['#1A2BFF', '#0E0E12', '#FAF4E7']} data-id="element-1180">
            <PrintCanvas bg="#EDEEF1" data-id="element-1181">
              <NameTapePatch data-id="element-1182" />
            </PrintCanvas>
          </ItemCard>
          <ItemCard number="MS · 03" title="Officer Badge" meta="CIRCULAR · 75MM" caption="Badge circular tipo oficial de transporte. Naranja brand, M monograma central y texto curvo arriba/abajo." inks={['#F26A35', '#0E0E12']} data-id="element-1183">
            <PrintCanvas bg="#FAF4E7" data-id="element-1184">
              <CircleBadgePatch data-id="element-1185" />
            </PrintCanvas>
          </ItemCard>
          <ItemCard number="MS · 04" title="Crew Shield" meta="ESCUDO · BORDADO" caption="Forma escudo con banda azul superior, M central y las dos líneas del sistema gráfico debajo." inks={['#0E0E12', '#1A2BFF', '#F26A35']} data-id="element-1186">
            <PrintCanvas bg="#DCDEE4" data-id="element-1187">
              <ShieldPatch data-id="element-1188" />
            </PrintCanvas>
          </ItemCard>
        </div>

        {/* ============================================================ */}
        {/* 12.2 — TEE PRINTS                                            */}
        {/* ============================================================ */}
        <CategoryHeader number="12.2" title="T-Shirt Prints." count="4 print artworks" desc="Estampaciones para camisetas oversized. Composiciones planas centradas, escala de pecho. Mezclan el logo PNG, los bullets de línea y el vocabulario transit (departures, route maps)." data-id="element-1189" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-20" data-id="element-1190">
          <ItemCard number="TS · 01" title="Wordmark Stack" meta="DTF · 1 TINTA" caption="El básico. Logo Metro centrado + listado de servicios en mono debajo. Para usar a diario." inks={['#1A2BFF']} data-id="element-1191">
            <PrintCanvas bg="#FFFFFF" data-id="element-1192">
              <TeePrintLogoStack data-id="element-1193" />
            </PrintCanvas>
          </ItemCard>
          <ItemCard number="TS · 02" title="Route Line" meta="SERIGRAFÍA · 3 TINTAS" caption="Diagrama de ruta horizontal con cinco paradas — un servicio por estación. La frase remata abajo." inks={['#F26A35', '#FFFFFF']} data-id="element-1194">
            <PrintCanvas bg="#0E0E12" textColor="#FFFFFF" data-id="element-1195">
              <TeePrintRouteMap data-id="element-1196" />
            </PrintCanvas>
          </ItemCard>
          <ItemCard number="TS · 03" title="Manifesto" meta="SERIGRAFÍA · 2 TINTAS" caption="Tipografía gigante a sangre. La frase de marca en tres líneas, con un acento naranja para activar el centro." inks={['#FFFFFF', '#F26A35']} data-id="element-1197">
            <PrintCanvas bg="#1A2BFF" data-id="element-1198">
              <TeePrintBigType data-id="element-1199" />
            </PrintCanvas>
          </ItemCard>
          <ItemCard number="TS · 04" title="Departures Board" meta="DTF · MONOCROMO" caption="Tabla de salidas como un panel de andén. Hora + servicio + línea, naranja sobre negro absoluto." inks={['#F26A35', '#E8C547']} data-id="element-1200">
            <PrintCanvas bg="#0E0E12" data-id="element-1201">
              <TeePrintDepartures data-id="element-1202" />
            </PrintCanvas>
          </ItemCard>
        </div>

        {/* ============================================================ */}
        {/* 12.3 — HOODIE BACK PRINTS                                    */}
        {/* ============================================================ */}
        <CategoryHeader number="12.3" title="Hoodie Back Prints." count="3 large-scale artworks" desc="Composiciones para la espalda de la sudadera. Escala grande, lectura desde 3m. Wordmark contorneado, roundel y manifesto editorial — tres registros distintos." data-id="element-1203" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-20" data-id="element-1204">
          <ItemCard number="HD · 01" title="Outline Wordmark" meta="SERIGRAFÍA · 1 TINTA BLANCA" caption="Letra solo en outline — sin relleno. Cita directa a la cultura streetwear: marca sin gritar." inks={['#FFFFFF']} data-id="element-1205">
            <PrintCanvas bg="#1A2BFF" textColor="#FFFFFF" aspect="aspect-[4/5]" data-id="element-1206">
              <HoodieBackOutline data-id="element-1207" />
            </PrintCanvas>
          </ItemCard>
          <ItemCard number="HD · 02" title="Transit Roundel" meta="SERIGRAFÍA · 2 TINTAS" caption="Roundel London Underground reinterpretado: anillo azul + barra horizontal con el wordmark." inks={['#1A2BFF', '#0E0E12']} data-id="element-1208">
            <PrintCanvas bg="#FAF4E7" aspect="aspect-[4/5]" data-id="element-1209">
              <HoodieBackRoundel data-id="element-1210" />
            </PrintCanvas>
          </ItemCard>
          <ItemCard number="HD · 03" title="Service Notice" meta="DTF · 2 TINTAS" caption="Manifiesto-aviso en formato edicto de transporte. Tres líneas, número de aviso, edition stamp." inks={['#FFFFFF', '#F26A35']} data-id="element-1211">
            <PrintCanvas bg="#0E0E12" textColor="#FFFFFF" aspect="aspect-[4/5]" data-id="element-1212">
              <HoodieBackManifesto data-id="element-1213" />
            </PrintCanvas>
          </ItemCard>
        </div>

        {/* ============================================================ */}
        {/* 12.4 — STICKERS                                              */}
        {/* ============================================================ */}
        <CategoryHeader number="12.4" title="Stickers." count="8 die-cut artworks" desc="Pack de stickers troquelados de vinilo mate. Inspirados en la señalética real de metros — bullets de línea (NYC), roundel (London), hexágono (Washington), blade de andén (BART), tarjeta de tarifa. El logo Metro PNG aparece como elemento hero en los stickers de marca." data-id="element-1214" />
        <div className="rounded-sm p-10 mb-20" style={{
        background: 'repeating-linear-gradient(45deg, #F4F5F7 0 10px, #EDEEF1 10px 20px)'
      }} data-id="element-1215">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-10 place-items-center" data-id="element-1216">
            <StickerLogoSquare data-id="element-1217" />
            <StickerLineBullets data-id="element-1218" />
            <StickerRoundel data-id="element-1219" />
            <StickerBlade data-id="element-1220" />
            <StickerMindTheBrief data-id="element-1221" />
            <StickerFareCard data-id="element-1222" />
            <StickerHexStation data-id="element-1223" />
            <StickerOvalAgency data-id="element-1224" />
          </div>
        </div>

        {/* ============================================================ */}
        {/* 12.5 — TOTE PRINTS                                           */}
        {/* ============================================================ */}
        <CategoryHeader number="12.5" title="Tote Bag Prints." count="4 large prints" desc="Cuatro artworks de gran formato para tote. Hero logo, mapa de red completo, panel de departures y monograma M gigante. Cada uno es un cartel portátil." data-id="element-1225" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-16" data-id="element-1226">
          <ItemCard number="TB · 01" title="Hero Logo" meta="SERIGRAFÍA · 1 TINTA" caption="Logo Metro centrado a tamaño grande + bajada institucional. La versión más limpia y reconocible." inks={['#1A2BFF']} data-id="element-1227">
            <PrintCanvas bg="#FAF4E7" data-id="element-1228">
              <TotePrintLogo data-id="element-1229" />
            </PrintCanvas>
          </ItemCard>
          <ItemCard number="TB · 02" title="Network Map" meta="SERIGRAFÍA · 4 TINTAS" caption="Mapa completo de la red Metro — cuatro líneas, estaciones nombradas, leyenda inferior. Es un póster en formato tote." inks={['#1A2BFF', '#F26A35', '#FFFFFF', '#E8C547']} data-id="element-1230">
            <PrintCanvas bg="#0E0E12" textColor="#FFFFFF" data-id="element-1231">
              <TotePrintMap data-id="element-1232" />
            </PrintCanvas>
          </ItemCard>
          <ItemCard number="TB · 03" title="Departures" meta="DTF · 1 TINTA AMARILLA" caption="Panel de salidas amarillo sobre negro, como los letreros LED de andén. Vintage transit feel." inks={['#E8C547']} data-id="element-1233">
            <PrintCanvas bg="#0E0E12" data-id="element-1234">
              <TotePrintDepartures data-id="element-1235" />
            </PrintCanvas>
          </ItemCard>
          <ItemCard number="TB · 04" title="Monogram M" meta="SERIGRAFÍA · 1 TINTA" caption="La letra M en italic chunky a tamaño bestial, ocupando todo el lienzo. Wordmark + ciudad como pie." inks={['#1A2BFF']} data-id="element-1236">
            <PrintCanvas bg="#FFFFFF" data-id="element-1237">
              <TotePrintMonogram data-id="element-1238" />
            </PrintCanvas>
          </ItemCard>
        </div>

        {/* ============================================================ */}
        {/* 12.6 — BUSINESS CARDS                                        */}
        {/* ============================================================ */}
        <CategoryHeader number="12.6" title="Business Cards." count="6 card designs" desc="Tarjetas de visita inspiradas en el ticket clásico de París, la silueta del vagón de metro y la señalética transit. Cada modelo es un formato distinto — papel, plástico, cartón — pensado para representar al estudio según el contexto." data-id="element-1239" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-20" data-id="element-1240">
          <ItemCard number="BC · 01" title="Paris T+ Ticket" meta="PAPEL 200GR · 30×65MM · FRONT/BACK" caption="Réplica directa del ticket parisino. Papel crema, tinta azul Metro, banda magnética sepia y chevron de inserción. Para entregar a clientes con gusto por el archivo." inks={['#1A2BFF', '#7A5638', '#F4ECD8']} data-id="element-1241">
            <PrintCanvas bg="#EDEEF1" pad="p-6" data-id="element-1242">
              <CardParisTicket data-id="element-1243" />
            </PrintCanvas>
          </ItemCard>

          <ItemCard number="BC · 02" title="Wagon Profile" meta="DIE-CUT · CARTÓN 350GR · 90×200MM" caption="Silueta de vagón de metro troquelada. Techo redondeado, cinco ventanas, dos puertas, número de coche y railes. La info del contacto vive dentro del vagón como pasajero." inks={['#1A2BFF', '#0E0E12', '#E8C547', '#FAF4E7']} data-id="element-1244">
            <PrintCanvas bg="#FAF4E7" pad="p-6" data-id="element-1245">
              <CardWagon data-id="element-1246" />
            </PrintCanvas>
          </ItemCard>

          <ItemCard number="BC · 03" title="Vertical Italic" meta="VERTICAL 55×85MM · PAPEL ALGODÓN" caption="Wordmark italic gigante ocupando la altura completa. Contacto reducido al pie. La más editorial — para conferencias y eventos de marca." inks={['#1A2BFF', '#E8C547', '#FFFFFF']} data-id="element-1247">
            <PrintCanvas bg="#EDEEF1" pad="p-8" data-id="element-1248">
              <CardVerticalItalic data-id="element-1249" />
            </PrintCanvas>
          </ItemCard>

          <ItemCard number="BC · 04" title="Fare Card" meta="PVC · CR80 85.6×54MM · FRONT/BACK" caption="Tarjeta tipo abono de transporte. Banda magnética, chip dorado, logo Metro y 'MEMBER SINCE'. Plástico real — para socios de estudio y partners." inks={['#1A2BFF', '#E8C547', '#0E0E12', '#FAF4E7']} data-id="element-1250">
            <PrintCanvas bg="#DCDEE4" pad="p-6" data-id="element-1251">
              <CardFare data-id="element-1252" />
            </PrintCanvas>
          </ItemCard>

          <ItemCard number="BC · 05" title="Roundel Card" meta="CUADRADA 70×70MM · OFFSET" caption="Formato cuadrado con roundel London Underground reinterpretado como hero. Contacto centrado abajo. La más reconocible a distancia." inks={['#1A2BFF', '#0E0E12', '#FAF4E7']} data-id="element-1253">
            <PrintCanvas bg="#EDEEF1" pad="p-8" data-id="element-1254">
              <CardRoundel data-id="element-1255" />
            </PrintCanvas>
          </ItemCard>

          <ItemCard number="BC · 06" title="Departures Card" meta="HORIZONTAL 90×55MM · NEGRO MATE" caption="Tarjeta-panel de salidas. Nombre tratado como una línea de departure — hora, destino (rol), plataforma (línea), status. Texto dot-matrix amarillo+naranja." inks={['#0E0E12', '#E8C547', '#F26A35']} data-id="element-1256">
            <PrintCanvas bg="#1F1F25" pad="p-6" data-id="element-1257">
              <CardDepartures data-id="element-1258" />
            </PrintCanvas>
          </ItemCard>
        </div>

        {/* Closing notes */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-12" data-id="element-1259">
          <div className="border border-[var(--divider)] rounded-sm p-5 bg-[var(--bg-surface)]" data-id="element-1260">
            <div className="font-mono text-[10px] text-[var(--text-muted)] tracking-widest mb-2" data-id="element-1261">
              VOCABULARIO
            </div>
            <div className="font-space font-bold text-lg leading-tight mb-2" data-id="element-1262">
              Diseño con sistema, no decoración.
            </div>
            <div className="text-[11px] text-[var(--text-muted)] leading-relaxed" data-id="element-1263">
              Cada artwork usa al menos un elemento del lenguaje transit —
              bullet, roundel, route map, blade, badge — combinado con el
              wordmark Metro. Sin ornamento gratuito.
            </div>
          </div>
          <div className="border border-[var(--divider)] rounded-sm p-5 bg-[var(--bg-surface)]" data-id="element-1264">
            <div className="font-mono text-[10px] text-[var(--text-muted)] tracking-widest mb-2" data-id="element-1265">
              PRODUCCIÓN
            </div>
            <div className="font-space font-bold text-lg leading-tight mb-2" data-id="element-1266">
              Archivo plano, listo para imprenta.
            </div>
            <div className="text-[11px] text-[var(--text-muted)] leading-relaxed" data-id="element-1267">
              Vectores con paleta de marca pura, máximo 4 tintas por pieza. Sin
              gradientes, sin sombras — todo serigrafiable o DTF en una pasada.
            </div>
          </div>
          <div className="border border-[var(--divider)] rounded-sm p-5 bg-[var(--text-ink)] text-white" data-id="element-1268">
            <div className="font-mono text-[10px] tracking-widest opacity-70 mb-2" data-id="element-1269">
              EDICIÓN
            </div>
            <div className="font-space font-bold text-lg leading-tight mb-2" data-id="element-1270">
              Tirada limitada. Numerada.
            </div>
            <div className="text-[11px] opacity-80 leading-relaxed" data-id="element-1271">
              50 unidades por colorway. Cada pieza llega con etiqueta de proof —
              número de tirada, fecha de impresión y línea del sistema (L-1,
              L-2, L-3).
            </div>
          </div>
        </div>
      </motion.div>
    </section>;
}